/**
 * Which documents the annotation viewer shows for one line item, and what to mark on each:
 *   - the SOURCE (the statement line, or the population row);
 *   - every EVIDENCE page / row the line matched (both pages of a combined match), with each value the attributes
 *     check marked on its own: amount, vendor, date, invoice number.
 * Colours give the result: green = agrees / supported, red = mismatch / not supported, amber = could not assess.
 * Everything comes from the stored item (`source`, `matches`), so it works for any file combination.
 */
import { formatAmount } from './lineItemModel';
import { itemMatchPlaces } from './itemFields';

const MAX_EVIDENCE_PANELS = 6;

/** "13746.00" -> ["13,746.00", "13746.00"]: the spellings an amount has on a page. */
export function amountVariants(v) {
  if (v == null || v === '') return [];
  const plain = String(v);
  const n = Number(plain);
  if (!Number.isFinite(n)) return [plain];
  return [...new Set([formatAmount(plain), n.toFixed(2)])];
}

const firstWord = (s) => (String(s || '').match(/[A-Za-z]{4,}/) || [])[0] || null;

const STATUS_COLOR = { passed: 'green', failed: 'red', warning: 'amber' };
/** The colour of the line as a whole. */
export const statusColor = (item) => STATUS_COLOR[item?.status] || 'amber';
/** The colour of one compared field (amount / party / date / reference) in a match. */
function fieldColor(match, field, item) {
  const c = (match?.compared || []).find((x) => x.field === field);
  if (c?.agrees === true) return 'green';
  // A field that reads differently on a line that passed (the statement says "VILLTD", the invoice "Vodafone Idea")
  // is a point to look at, not an exception: red is kept for lines that failed.
  if (c?.agrees === false) return item?.status === 'failed' ? 'red' : 'amber';
  return field === 'amount' ? statusColor(item) : 'amber';
}

/** What the AI annotator is told when a page has no text layer to mark. It draws pass/fail tickmarks from the
 *  findings, so they state this line's real result and ask for one row only (no invented exceptions). */
function findingsFor(item, role, places) {
  const ref = item?.item_ref || '';
  const passed = item?.status === 'passed';
  const where = places ? ` Supported by: ${places}.` : '';
  if (role === 'source') {
    return passed
      ? `PASS. Mark ONLY the transaction row "${ref}" with a green tick and a short callout "Supported by invoice".${where} Exception rows: none. Do not mark any other row and do not raise any exception.`
      : `EXCEPTION. Mark ONLY the transaction row "${ref}" with a red cross. ${item?.observation || 'No supporting document was found for it.'} Do not mark any other row.`;
  }
  if (!passed) {
    return `EXCEPTION. This page was matched to the statement line "${ref}" but ${item?.observation || 'it does not fully support it'}. Mark the amount, vendor and date with a red cross where they disagree. Do not mark any other row.`;
  }
  return `PASS. This page supports the statement line "${ref}". Mark the amount, vendor name, invoice date and invoice number that match it with a green tick and a short callout "Matches the statement line". Exception rows: none.`;
}

function sourcePanel(item, places) {
  const src = item?.source;
  const loc = src?.location;
  if (!src || !loc?.file) return null;
  const texts = amountVariants(src.amount);
  if (!texts.length && src.reference) texts.push(src.reference);
  if (!texts.length) texts.push(item.item_ref);
  return {
    key: 'source',
    role: 'source',
    title: loc.file,
    file: loc.file, fileId: loc.file_id ?? null, page: loc.page_number ?? null, row: loc.row_number ?? null, sheet: loc.sheet_name ?? null,
    excerpt: loc.excerpt || item.item_ref,
    findings: findingsFor(item, 'source', places),
    targets: [{ texts, anchors: [src.date, firstWord(src.party)].filter(Boolean), color: statusColor(item) }],
  };
}

function evidenceTexts(item, match) {
  const parts = match?.found_parts?.length ? match.found_parts : [match?.found_amount ?? item?.source?.amount];
  return [...new Set(parts.flatMap(amountVariants).filter(Boolean))];
}

/** One target per value the attributes check, each coloured by whether it agrees: the amount, the vendor, the date
 *  and the invoice / reference number (the found value when the match recorded one, else the source's). */
export function evidenceTargets(item, match) {
  const found = (field) => (match?.compared || []).find((x) => x.field === field)?.found;
  const src = item?.source || {};
  const targets = [{ texts: evidenceTexts(item, match), anchors: [], color: fieldColor(match, 'amount', item) }];
  const vendor = found('party') || firstWord(src.party);
  if (vendor) targets.push({ texts: [vendor], anchors: [], color: fieldColor(match, 'party', item) });
  const date = found('date');
  if (date) targets.push({ texts: [date], anchors: [], color: fieldColor(match, 'date', item) });
  const ref = found('reference') || src.reference;
  if (ref) targets.push({ texts: [ref], anchors: [], color: fieldColor(match, 'reference', item) });
  return targets.filter((t) => t.texts.length);
}

export function buildAnnotationPanels(item) {
  const panels = [];
  const places = itemMatchPlaces(item).map((p) => `${p.file}${p.pages.length ? ` p${p.pages.join(', ')}` : ''}`).join('; ');
  const src = sourcePanel(item, places);
  if (src) panels.push(src);

  const matches = (item?.matches || []).filter((m) => m.status === 'matched' || m.status === 'combined');
  const evidence = [];
  const cited = (item?.citation?.source_file || '').toLowerCase();
  matches.forEach((m) => {
    (m.locations || []).forEach((l) => {
      evidence.push({
        file: m.file, fileId: m.file_id ?? l.file_id ?? null, page: l.page_number ?? null, row: l.page_number == null ? (l.row_number ?? null) : null,
        sheet: l.sheet_name ?? null, excerpt: l.excerpt || '', targets: evidenceTargets(item, m),
      });
    });
    // The other pages the line was matched on in this file (an invoice's next pages, its payment receipt).
    if (cited && m.file?.toLowerCase() === cited) {
      const shown = new Set((m.locations || []).map((l) => l.page_number));
      (item.matched_pages || []).filter((p) => !shown.has(p)).forEach((page) => evidence.push({
        file: m.file, fileId: m.file_id ?? null, page, row: null, sheet: null, excerpt: '', targets: evidenceTargets(item, m),
      }));
    }
  });
  // By file (in the order first seen), then page, then row.
  const fileOrder = [...new Set(evidence.map((e) => e.file))];
  evidence.sort((a, b) => (fileOrder.indexOf(a.file) - fileOrder.indexOf(b.file))
    || ((a.page ?? Infinity) - (b.page ?? Infinity)) || ((a.row ?? Infinity) - (b.row ?? Infinity)));
  if (!evidence.length) {
    // Runs stored before matches existed: the file and pages are in matched_to, its id only on the citation.
    const only = itemMatchPlaces(item);
    only.forEach((p) => {
      const pages = p.pages.length ? p.pages : [null];
      pages.forEach((page) => evidence.push({
        file: p.file, fileId: only.length === 1 ? (item.citation?.source_file_id ?? null) : null, page, row: null, sheet: null, excerpt: '',
        targets: evidenceTargets(item, null),
      }));
    });
  }
  evidence.slice(0, MAX_EVIDENCE_PANELS).forEach((e, i) => panels.push({
    key: `evidence-${i}`, role: 'evidence', title: e.file, findings: findingsFor(item, 'evidence', ''), ...e,
  }));
  return { panels, hiddenEvidence: Math.max(0, evidence.length - MAX_EVIDENCE_PANELS) };
}

/**
 * The documents behind a sample that has no line items: every place its attributes' results cite in the evidence
 * (one panel per file and page, the attributes that cite it in its title). Runs that cite nothing fall back to the
 * files mapped to the sample (`fileRefs`), so a sample with evidence always has something to show. Nothing is
 * marked from text: the page annotated when the run finished is shown, else the AI annotator draws on the page
 * from the attributes' findings, else the plain page.
 */
const MAX_HIGHLIGHT_TARGETS = 12;
const GENERIC_PHRASES = /^(the|and|of|for|to|from|dated?|date|re|fwd|subject|regards|thanks|hi|dear|approved|tax invoice|invoice( no\.?| number)?|bill of supply)$/i;

/** Short phrases of a cited excerpt that can be found verbatim on its page: "Invoice No. : 18/2025-26, Dated : 1st
 * September, 2025" -> ["18/2025-26", "1st September"]. Labels, bare years and long sentences are left out. */
export function excerptPhrases(excerpt) {
  return [...new Set(String(excerpt || '')
    .split(/\s*(?:\.\.\.|…|[,;|<>()]|\s:\s?|:\s|\s[-–]\s)\s*/)
    .map((x) => x.trim().replace(/^['"]|['"]$/g, '').replace(/[.:]$/, '').trim())
    .filter((x) => x.length >= 5 && x.length <= 60 && !/^\d{4}$/.test(x) && !GENERIC_PHRASES.test(x)))];
}

/** What to mark on a cited page: each citation's phrases, coloured by the result that cited it and labelled with
 * its attribute, so the page reads "A ✓" / "E !" beside what it marks. A phrase two attributes cite is one target. */
function citationTargets(cites) {
  const targets = [];
  const byText = new Map();
  cites.forEach(({ excerpt, status, ref }) => excerptPhrases(excerpt).forEach((text) => {
    const color = STATUS_COLOR[status] || 'amber';
    const known = byText.get(`${text.toLowerCase()}|${color}`);
    if (known) {
      if (ref && !known.label.split(', ').includes(ref)) known.label = [known.label, ref].filter(Boolean).join(', ');
      return;
    }
    if (targets.length >= MAX_HIGHLIGHT_TARGETS) return;
    const t = { texts: [text], anchors: [], color, label: ref || '' };
    byText.set(`${text.toLowerCase()}|${color}`, t);
    targets.push(t);
  }));
  return targets;
}

export function buildCitationPanels(results, fileRefs = []) {
  const byPlace = new Map();
  const add = (c, r) => {
    if (!c?.source_file_id) return;
    const key = [c.source_file_id, c.sheet_name || '', c.page_number ?? ''].join('|');
    if (!byPlace.has(key)) {
      byPlace.set(key, {
        file: c.source_file || '', fileId: c.source_file_id, page: c.page_number ?? null,
        row: c.page_number == null && c.row_numbers?.length ? c.row_numbers[0] + 1 : null,
        sheet: c.sheet_name ?? null, excerpt: c.excerpt || '', results: [], cites: [],
        mapped: true,   // until a citation the AI made reaches this place (below)
        // exactly what the run annotated, so its saved page can be looked up
        saved: { source_file: c.source_file || '', source_file_id: c.source_file_id, sheet_name: c.sheet_name ?? null,
          page_number: c.page_number || 1, row_numbers: c.row_numbers?.length ? c.row_numbers : null, excerpt: c.excerpt || '' },
      });
    }
    const place = byPlace.get(key);
    // Mapped (not cited) only while every citation here is a stand-in: one real citation makes it the AI's evidence.
    if (r && c.fallback !== true) place.mapped = false;
    if (!place.excerpt && c.excerpt) place.excerpt = c.excerpt;
    if (r && !place.results.includes(r)) place.results.push(r);
    if (c.excerpt) place.cites.push({ excerpt: c.excerpt, status: r?.status, ref: r?.attr?.ref });
  };
  (results || []).forEach((r) => [r.cell?.citation, ...(r.cell?.citations || [])].forEach((c) => add(c, r)));
  if (!byPlace.size) {
    const isPaged = (name) => /\.(pdf|png|jpe?g|webp|gif|bmp|tiff?|heic|heif)$/i.test(String(name || ''));
    (fileRefs || []).forEach((f) => add({ source_file: f.file, source_file_id: f.fileId, page_number: isPaged(f.file) ? 1 : null }, null));
    byPlace.forEach((p) => { p.results = (results || []).slice(); });
  }
  const places = [...byPlace.values()];
  const panels = places.slice(0, MAX_EVIDENCE_PANELS).map((p, i) => {
    const findings = p.results.map((r) => {
      const verdict = r.status === 'passed' ? 'PASS' : r.status === 'failed' ? 'EXCEPTION' : 'COULD NOT ASSESS';
      return `${r.attr.ref} (${r.attr.label || r.attr.ref}): ${verdict}. ${r.cell?.observation || ''}`.trim();
    }).join('\n');
    return {
      key: `citation-${i}`, role: 'evidence', title: p.file, file: p.file, fileId: p.fileId, page: p.page, row: p.row, sheet: p.sheet,
      // aiFirst: the AI annotation (ticks and notes drawn on the page) is tried before these marks, which stand in
      // when it is unavailable (no image-model credit, an outage).
      excerpt: p.excerpt, targets: citationTargets(p.cites), aiFirst: true, mapped: p.mapped, attrRefs: p.results.map((r) => r.attr.ref), saved: p.saved,
      findings: `${findings}\nMark only what these findings describe: green ticks for what is supported, red crosses for exceptions.`,
    };
  });
  return { panels, hiddenEvidence: Math.max(0, places.length - MAX_EVIDENCE_PANELS) };
}

/** "Bank Statement.pdf · p1" / "approvals.xlsx · row 3" */
export function panelCaption(p) {
  const where = p.page != null ? `p${p.page}` : p.row != null ? `row ${p.row}` : '';
  return `${p.file}${where ? ` · ${where}` : ''}`;
}
